import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";

import { libraryService, type Book, type BookCopy, type EligibleStudent } from "../../services/libraryService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { LOAN_DAYS, defaultDueDate, labelFrom, CONDITIONS } from "./libraryLogic";
import { SearchPick } from "./Pickers";

const pupilDetail = (student: EligibleStudent) => [student.student_id, student.current_class_name].filter(Boolean).join(" · ");

/** Lend a copy to a pupil. Only copies on the shelf can be chosen; a held copy goes out through its reservation. */
export function IssueForm({ students, copies, bookId, onClose, onDone }: {
  students: EligibleStudent[];
  copies: BookCopy[];
  /** Narrow the copies to one title, when issuing from the catalogue. */
  bookId?: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const shelf = copies.filter((copy) => copy.status === "available" && (!bookId || copy.book === bookId));
  const [student, setStudent] = useState("");
  const [copy, setCopy] = useState(shelf.length === 1 ? String(shelf[0].id) : "");
  const [due, setDue] = useState(defaultDueDate());
  const maxDue = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const issue = useMutation({
    // The server wants a moment, not a day: the end of the school day.
    mutationFn: () => libraryService.borrow({ book_copy_id: Number(copy), student_id: Number(student), due_date: `${due}T16:00:00` }),
    onSuccess: (loan) => onDone(`${loan.book_title} lent to ${loan.borrower_name}, due ${loan.due_date.slice(0, 10)}.`),
  });
  return (
    <FormModal
      id="issue-form"
      title="Lend a book"
      busy={issue.isPending}
      error={issue.isError ? apiErrorMessage(issue.error, "The book could not be lent.") : undefined}
      saveLabel="Lend it"
      note={`Loans run ${LOAN_DAYS} days and can be renewed twice.`}
      onClose={onClose}
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (student && copy) issue.mutate();
      }}
    >
      <FormSection title="Who and what" divided={false} columns={1}>
        <Field as="div" label="Pupil">
          <SearchPick
            required
            items={students}
            value={student}
            onChange={setStudent}
            label={(item) => item.full_name}
            detail={pupilDetail}
            placeholder="Type a name, class or pupil number"
            empty="No pupil matches"
          />
        </Field>
        <Field as="div" label="Copy" hint={shelf.length ? `${shelf.length} ${shelf.length === 1 ? "copy is" : "copies are"} on the shelf.` : "No copy is on the shelf. A held copy is lent from its reservation."}>
          <SearchPick
            required
            items={shelf}
            value={copy}
            onChange={setCopy}
            label={(item) => `${item.book_title} — copy ${item.copy_number}`}
            text={(item) => `${item.book_title} ${item.book_authors ?? ""} ${item.barcode ?? ""} ${item.copy_number}`}
            detail={(item) => [item.barcode, labelFrom(CONDITIONS, item.condition)].filter(Boolean).join(" · ")}
            placeholder="Type a title or scan a barcode"
            empty="No copy on the shelf matches"
          />
        </Field>
        <Field label="Due back" hint="At most 90 days away.">
          <input required type="date" min={today} max={maxDue} value={due} onChange={(event) => setDue(event.target.value)} className="input mt-1 sm:max-w-xs" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Join the queue for a title. Pupils reserve for themselves, parents for their children, staff for any pupil. */
export function ReserveForm({ students, books, bookId, onClose, onDone }: {
  students: EligibleStudent[];
  books: Book[];
  bookId?: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [student, setStudent] = useState(students.length === 1 ? String(students[0].id) : "");
  const [book, setBook] = useState(bookId ? String(bookId) : "");
  const [days, setDays] = useState("7");
  const reserve = useMutation({
    mutationFn: () => libraryService.reserve({ book_id: Number(book), student_id: Number(student), expiry_days: Number(days) }),
    onSuccess: (reservation) => onDone(`${reservation.book_title} reserved for ${reservation.student_name}: number ${reservation.priority} in the queue.`),
  });
  return (
    <FormModal
      id="reserve-form"
      title="Reserve a book"
      busy={reserve.isPending}
      error={reserve.isError ? apiErrorMessage(reserve.error, "The book could not be reserved.") : undefined}
      saveLabel="Reserve it"
      note="The library holds a copy for the first pupil in the queue."
      onClose={onClose}
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (student && book) reserve.mutate();
      }}
    >
      <FormSection title="Reservation" divided={false} columns={1}>
        {students.length === 1 ? (
          <Field label="For">
            <input readOnly value={students[0].full_name} className="input mt-1 bg-gray-50" />
          </Field>
        ) : (
          <Field as="div" label="For">
            <SearchPick required items={students} value={student} onChange={setStudent} label={(item) => item.full_name} detail={pupilDetail} placeholder="Type a name or class" empty="No pupil matches" />
          </Field>
        )}
        <Field as="div" label="Book">
          <SearchPick
            required
            items={books}
            value={book}
            onChange={setBook}
            label={(item) => item.title}
            text={(item) => `${item.title} ${item.authors_display} ${item.isbn ?? ""}`}
            detail={(item) => item.authors_display}
            placeholder="Type a title or author"
            empty="No title matches"
          />
        </Field>
        <Field label="Keep the reservation for" hint="If no copy is collected by then, the next pupil moves up.">
          <select value={days} onChange={(event) => setDays(event.target.value)} className="input mt-1 sm:max-w-xs">
            {[3, 7, 14, 30].map((value) => <option key={value} value={value}>{value} days</option>)}
          </select>
        </Field>
      </FormSection>
    </FormModal>
  );
}
